/**
 * "Basics" tab — project details (read-only subscription, resource group), instance
 * details (VM name, region, availability, security type, image, size), administrator
 * account, inbound ports (fixed: none) and licensing.
 *
 * With "Use existing" only the VM name, resource group and OS are shown: the server is
 * only verified through the Azure control plane, nothing is created.
 */
import { RadioField, ReadOnlyField, TextField } from './FormFields'
import type { AppriseAvailabilityType, AppriseDeploymentMode, AppriseOsType, AppriseSecurityType } from '@/types'
import { Banner } from './AppriseFields'
import { AppriseAdminAccount } from './AppriseAdminAccount'
import { ImagePicker, SizePicker } from './AppriseImageSizeFields'
import { CheckField, Combobox, FieldFrame, LiveSelectField, type ComboOption } from './LiveSelect'
import { SECURITY_TYPE_LABELS } from './appriseReview'
import styles from './AppriseCreateVm.module.css'
import type { AppriseVmFormApi } from './useAppriseVmForm'

const AVAILABILITY_LABELS: Record<string, string> = {
  none: 'No infrastructure redundancy required', zone: 'Availability zone', availability_set: 'Availability set',
  vmss: 'Virtual machine scale set',
}

function SubscriptionField({ form }: { form: AppriseVmFormApi }) {
  const { info, isLoading, error, blocker } = form.live.subscription
  const value = info?.name ?? (isLoading ? 'Checking the pinned subscription…' : 'Unknown')
  return (
    <>
      {blocker && <Banner tone="error" role="alert">{blocker}</Banner>}
      {!blocker && error && (
        <Banner tone="warning" role="status">
          Could not read the pinned subscription: {error}. The server still refuses to plan or create anything if it is not resolved.
        </Banner>
      )}
      <ReadOnlyField label="Subscription" value={value}
        hint={info ? `Pinned by your administrator — not editable${info.signed_in_as ? ` · signed in as ${info.signed_in_as}` : ''}` : 'Pinned by your administrator — not editable'} />
    </>
  )
}

function ResourceGroupField({ form }: { form: AppriseVmFormApi }) {
  const s = form.config.server
  const groups = form.live.resourceGroups
  const existing = s.resource_group_mode === 'existing'
  const typed = s.resource_group_name.trim().toLowerCase()
  // An exact match shows the whole list (so the user can switch group without clearing the field first).
  const exact = groups.items.some((g) => g.name.toLowerCase() === typed)
  const options: ComboOption[] = groups.items
    .filter((g) => !typed || exact || g.name.toLowerCase().includes(typed))
    .map((g) => ({ id: g.name, label: g.name, description: g.location, selected: g.name.toLowerCase() === typed }))
  return (
    <FieldFrame label="Resource group" required catalog={groups} error={form.validation.errors['server.resource_group_name']}
      hint={s.resource_group_name.trim()
        ? (s.deployment_mode === 'use_existing' ? 'Resource group of the existing VM'
          : existing ? 'Existing resource group — the VM is created in it'
          : 'Not in the list — a new resource group will be created')
        : 'Pick an existing group or type a new name'}>
      <Combobox ariaLabel="Resource group" mono inputValue={s.resource_group_name}
        invalid={!!form.validation.errors['server.resource_group_name']}
        onInputChange={(v) => form.setServer({ resource_group_name: v })}
        onPick={(name) => form.setServer({ resource_group_name: name })}
        options={options} loading={groups.isLoading}
        emptyText="No existing group matches — the name you typed will be created" />
    </FieldFrame>
  )
}

function AvailabilityFields({ form }: { form: AppriseVmFormApi }) {
  const a = form.config.server.availability
  const live = form.live.availability
  const err = form.validation.errors
  const option = live.items.find((o) => o.id === a.type)
  return (
    <>
      <LiveSelectField label="Availability options" value={a.type} catalog={live}
        valueLabel={AVAILABILITY_LABELS[a.type]} error={err['server.availability.type']}
        options={live.items.map((o) => ({ value: o.id, label: o.label }))}
        onChange={(v) => form.setAvailability({ type: v as AppriseAvailabilityType })} />
      {a.type === 'zone' && (
        <LiveSelectField label="Availability zone" required value={a.zone} error={err['server.availability.zone']}
          placeholder="Choose a zone…" options={(option?.zones ?? []).map((z) => ({ value: z, label: `Zone ${z}` }))}
          onChange={(v) => form.setAvailability({ zone: v as '1' | '2' | '3' })} />
      )}
      {(a.type === 'availability_set' || a.type === 'vmss') && (
        <LiveSelectField
          label={a.type === 'vmss' ? 'Virtual machine scale set' : 'Availability set'} required
          value={a.type === 'vmss' ? a.vmss_id : a.availability_set_id}
          error={err[a.type === 'vmss' ? 'server.availability.vmss_id' : 'server.availability.availability_set_id']}
          emptyText="None found in this region"
          placeholder="Choose one…" options={(option?.sets ?? []).map((x) => ({ value: x.id, label: `${x.name} (${x.resource_group})` }))}
          onChange={(v) => form.setAvailability(a.type === 'vmss' ? { vmss_id: v } : { availability_set_id: v })} />
      )}
    </>
  )
}

function SecurityTypeFields({ form }: { form: AppriseVmFormApi }) {
  const sec = form.config.security
  const live = form.live.securityTypes
  const options = live.items.map((o) => ({
    value: o.id,
    label: `${SECURITY_TYPE_LABELS[o.id] ?? o.id}${o.supported ? '' : ` — ${(o.reason || 'not supported').replace(/^./, (c) => c.toLowerCase())}`}`,
    disabled: !o.supported,
  }))
  return (
    <>
      <LiveSelectField label="Security type" value={sec.security_type} catalog={live} options={options}
        valueLabel={SECURITY_TYPE_LABELS[sec.security_type]} error={form.validation.errors['security.security_type']}
        onChange={(v) => form.setSecurity({ security_type: v as AppriseSecurityType })} />
      {sec.security_type === 'trusted_launch' && (
        <>
          <CheckField label="Enable secure boot" checked={sec.secure_boot} onChange={(v) => form.setSecurity({ secure_boot: v })} full={false} />
          <CheckField label="Enable vTPM" checked={sec.vtpm} onChange={(v) => form.setSecurity({ vtpm: v })} full={false} />
        </>
      )}
    </>
  )
}

function Licensing({ form }: { form: AppriseVmFormApi }) {
  const s = form.config.server
  const on = s.license_type === 'windows_server'
  return (
    <>
      <div className={styles.sectionTitle}>Licensing</div>
      <CheckField label="Use an existing Windows Server license (Azure Hybrid Benefit)"
        description="Pay only for the compute — your Windows Server license is applied to this VM."
        checked={on} onChange={(v) => form.setServer({ license_type: v ? 'windows_server' : 'none', license_confirmed: false })} />
      {on && (
        <CheckField label="I confirm I have an eligible Windows Server license with Software Assurance or a Windows Server subscription to apply this Azure Hybrid Benefit."
          checked={s.license_confirmed} onChange={(v) => form.setServer({ license_confirmed: v })}
          error={form.validation.errors['server.license_confirmed']} />
      )}
    </>
  )
}

function ExistingServer({ form }: { form: AppriseVmFormApi }) {
  const s = form.config.server
  return (
    <>
      <Banner>
        Using an existing server: nothing is created and Terraform does not run. The deployment only verifies the VM through the Azure
        control plane. Only the VM name, resource group and operating system are needed.
      </Banner>
      <RadioField<AppriseOsType> name="ap_os" label="Operating System" required value={s.os_type} error={form.validation.errors['server.os_type']}
        options={[{ value: 'windows', label: 'Windows' }, { value: 'linux', label: 'Linux' }]}
        onChange={(v) => form.setServer({ os_type: v })} />
    </>
  )
}

export function AppriseBasicsTab({ form, onRegionChange }: { form: AppriseVmFormApi; onRegionChange: (region: string) => void }) {
  const s = form.config.server
  const err = form.validation.errors
  const existing = s.deployment_mode === 'use_existing'
  const locations = form.live.locations

  return (
    <>
      <RadioField<AppriseDeploymentMode> name="ap_deploy_mode" label="Virtual machine" value={s.deployment_mode} full
        options={[{ value: 'create_new', label: 'Create a new virtual machine' }, { value: 'use_existing', label: 'Use an existing virtual machine (verify only)' }]}
        onChange={(v) => form.setServer({ deployment_mode: v })} />

      <div className={styles.sectionTitle}>Project details</div>
      <SubscriptionField form={form} />
      <ResourceGroupField form={form} />

      <div className={styles.sectionTitle}>Instance details</div>
      <TextField label="Virtual machine name" required mono value={s.server_name} error={err['server.server_name'] ?? err['server.hostname']}
        placeholder="Enter preferred name"
        hint={existing ? 'Name of the existing Azure VM' : s.os_type === 'windows' ? 'Up to 15 characters: it is also the Windows computer name' : undefined}
        onChange={(v) => form.setServer({ server_name: v })} />
      {existing && <ExistingServer form={form} />}
      {!existing && (
        <>
          <LiveSelectField label="Region" required value={form.region} catalog={locations} onChange={onRegionChange}
            options={locations.items.map((l) => ({ value: l.display_name, label: l.display_name }))} />
          <AvailabilityFields form={form} />
          <SecurityTypeFields form={form} />
          <ImagePicker form={form} />
          <SizePicker form={form} />
          <AppriseAdminAccount form={form} />
          <div className={styles.sectionTitle}>Inbound port rules</div>
          <ReadOnlyField label="Public inbound ports" value="None"
            hint="Fixed — no inbound port is opened. To RDP/SSH, add an NSG rule, use Azure Bastion or JIT after creation." />
          {s.os_type === 'windows' && <Licensing form={form} />}
        </>
      )}
    </>
  )
}
